import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Globe } from 'lucide-react';
import { LOGO_DARK } from '../App';
import { useTranslation } from '../context/LanguageContext';
import { LANGUAGES, useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config/api';

// Default content if DB is empty
const DEFAULT_TERMS = `
<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">1. Accettazione dei Termini</h2>
  <p class="text-gray-700 leading-relaxed">
    Utilizzando Slimmy.com ("Servizio"), accetti di essere vincolato da questi Termini di Servizio. 
    Se non accetti questi termini, non utilizzare il Servizio. Ci riserviamo il diritto di modificare 
    questi termini in qualsiasi momento.
  </p>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">2. Descrizione del Servizio</h2>
  <p class="text-gray-700 leading-relaxed">
    Slimmy.com è un'applicazione per il monitoraggio del peso e il benessere che offre:
  </p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2 mt-3">
    <li>Tracciamento del peso e progressi</li>
    <li>Piani alimentari personalizzati generati dall'intelligenza artificiale</li>
    <li>Sfide di gruppo per la motivazione</li>
    <li>Feed social per condividere i progressi</li>
    <li>Tracciamento GPS delle camminate</li>
    <li>Ricette suggerite da Chef AI</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">3. Account Utente</h2>
  <p class="text-gray-700 leading-relaxed mb-3">Per utilizzare il Servizio devi:</p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2">
    <li>Avere almeno 16 anni di età</li>
    <li>Fornire informazioni accurate e complete</li>
    <li>Mantenere la sicurezza del tuo account</li>
    <li>Essere responsabile di tutte le attività che avvengono sotto il tuo account</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">4. Uso Accettabile</h2>
  <p class="text-gray-700 leading-relaxed mb-3">Accetti di NON:</p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2">
    <li>Utilizzare il Servizio per scopi illegali</li>
    <li>Pubblicare contenuti offensivi, diffamatori o inappropriati</li>
    <li>Impersonare altre persone</li>
    <li>Interferire con il funzionamento del Servizio</li>
    <li>Tentare di accedere a dati di altri utenti</li>
    <li>Utilizzare bot o script automatizzati</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">5. Disclaimer Medico</h2>
  <p class="text-gray-700 leading-relaxed font-medium bg-yellow-50 p-4 rounded-lg border border-yellow-200">
    ⚠️ <strong>IMPORTANTE:</strong> Slimmy.com NON è un servizio medico. Le informazioni fornite, 
    inclusi piani alimentari e suggerimenti, sono solo a scopo informativo e non sostituiscono 
    il parere di un medico o professionista sanitario.
  </p>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">6. Limitazione di Responsabilità</h2>
  <p class="text-gray-700 leading-relaxed">
    Il Servizio è fornito "così com'è" senza garanzie di alcun tipo. Slimmy.com non sarà 
    responsabile per danni derivanti dall'uso del Servizio.
  </p>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">7. Contatti</h2>
  <p class="text-gray-700 leading-relaxed">
    Per domande: <strong>info@slimmy.app</strong>
  </p>
</section>
`;

export default function Terms() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [content, setContent] = useState(null);
  const [updated, setUpdated] = useState('Marzo 2026');
  const [loading, setLoading] = useState(true);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const loadTerms = async () => {
      try {
        const res = await fetch(`${API_URL}/api/legal/terms?lang=${language}`);
        const data = await res.json();
        if (data.content) {
          setContent(data.content);
          if (data.updated) setUpdated(data.updated);
        } else {
          setContent(DEFAULT_TERMS);
        }
      } catch (err) {
        console.error('Error loading terms:', err);
        setContent(DEFAULT_TERMS);
      } finally {
        setLoading(false);
      }
    };
    loadTerms();
  }, [language]);

  return (
    <div className="fixed inset-0 bg-white overflow-y-auto">
      {/* Header with safe area */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="terms-back-btn"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <img src={LOGO_DARK} alt="Slimmy" className="h-8" />
          <div className="ml-auto relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="terms-lang-btn"
              aria-label={t('legal.language') || 'Language'}
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>
            {langOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-20 cursor-default"
                  onClick={() => setLangOpen(false)}
                  aria-label="close"
                  tabIndex={-1}
                />
                <div
                  className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-30"
                  data-testid="terms-lang-menu"
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                        l.code === language ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-700'
                      }`}
                      data-testid={`terms-lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content — the backend HTML content already includes its own H1
          and "Last updated" line, so we don't render them locally to
          avoid the duplicate header bug. */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div 
            className="prose prose-gray max-w-none space-y-6"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}

        <div className="mt-12 pt-6 pb-12 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            © 2026 Slimmy.com - {t('legal.allRightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
}
